import type { AssetCollection } from "@pax/api-contract/assets/v1alpha1";
import { cn, focusHaloFlush, press, Skeleton, TruncatedText } from "@pax/ui";
import { Link } from "@tanstack/react-router";
import { BadgeCheck, Folder, Lock } from "lucide-react";
import { type CSSProperties, useState } from "react";
import { averageOf, collectionPictureOf, type AssetPicture as Picture } from "../media";
import { CAPTION, Masonry, TITLE_CHARACTER, TITLE_LINE, wraps } from "./AssetGrid";
import { AssetPicture } from "./AssetPicture";

/** The tallest and widest a cover is drawn, as width over height. Beyond them it is cropped to fit. */
const TALLEST_COVER = 3 / 4;
const WIDEST_COVER = 2;
/** The shape of a card with no cover yet. */
const EMPTY_COVER = 4 / 3;
/** How far the sheets show above the cover, as a share of the card's width. */
const SHEETS = 0.032;

/** The shape a collection's card is drawn at: its cover's own, within the tallest and widest. */
function coverShape(cover: Picture | null): number {
  if (!cover) return EMPTY_COVER;
  return Math.min(WIDEST_COVER, Math.max(TALLEST_COVER, cover.width / cover.height));
}

/** How tall a collection's card is in a column this wide: sheets, cover, and caption. */
function collectionHeight(collection: AssetCollection, columnWidth: number): number {
  const cover = collection.cover && collectionPictureOf(collection.cover);
  return (
    columnWidth * SHEETS +
    columnWidth / coverShape(cover) +
    CAPTION +
    wraps(collection.title, columnWidth - 20, TITLE_CHARACTER, TITLE_LINE)
  );
}

/** Collections in columns, as the asset library lays out assets, each card at its cover's shape. */
export function CollectionGrid({ collections }: { collections: AssetCollection[] }) {
  return (
    <Masonry
      items={collections}
      keyOf={collection => collection.id}
      heightOf={collectionHeight}
      render={(collection, columnWidth) => (
        <CollectionCard collection={collection} sizes={`${Math.ceil(columnWidth)}px`} />
      )}
    />
  );
}

/**
 * The sheets a collection's cover stands on, showing above it so the card
 * reads as several things rather than one. Each is the cover's own overall
 * colour, blended further into the page the further back it is, with a
 * hairline of light along its top edge and corners that nest inside the
 * cover's. Pointing at the card fans them up a little.
 */
function Sheets({ thumbhash }: { thumbhash: string | null }) {
  const tint = thumbhash ? averageOf(thumbhash).colour : "var(--foreground)";
  const sheet = (share: string): CSSProperties => ({
    background: `color-mix(in oklab, ${tint} ${share}, var(--background))`,
  });
  const edge =
    "absolute h-1/2 shadow-[inset_0_1px_0_rgb(255_255_255/0.7)] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] dark:shadow-[inset_0_1px_0_rgb(255_255_255/0.07)]";
  return (
    <>
      <span
        aria-hidden
        style={sheet(thumbhash ? "var(--sheet-far)" : "5%")}
        className={cn(
          edge,
          "inset-x-[7%] top-0 rounded-[11px] motion-safe:group-hover:-translate-y-[2.2cqw]",
        )}
      />
      <span
        aria-hidden
        style={sheet(thumbhash ? "var(--sheet-near)" : "9%")}
        className={cn(
          edge,
          "inset-x-[3.5%] top-[1.6cqw] rounded-[13.5px] motion-safe:group-hover:-translate-y-[1.1cqw]",
        )}
      />
    </>
  );
}

/**
 * A collection as its cover on a stack of sheets, with its name under it: the
 * picture its owner chose, or else the first one added. The card takes the
 * cover's own shape, as an asset's does, so a flag or a portrait is whole; only
 * a cover taller than 3:4 or wider than 2:1 is cropped, so one banner can't
 * take over a column. The name goes under the picture rather than over it, so
 * nothing of the cover is hidden behind it.
 *
 * A sensitive cover, which only an owner's choice can be, is its thumbhash
 * until it is pointed at or focused, as a sensitive picture is in the library.
 */
function CollectionCard({
  collection,
  sizes = "240px",
  className,
}: {
  collection: AssetCollection;
  /** How wide the card is drawn, for choosing which file of the cover to load. */
  sizes?: string;
  className?: string;
}) {
  const cover = collection.cover && collectionPictureOf(collection.cover);
  const sensitive = collection.cover?.sensitive === true;
  const [lookedAt, setLookedAt] = useState(false);
  return (
    <Link
      to="/assets/collections/$collectionId"
      params={{ collectionId: collection.id }}
      onFocus={() => setLookedAt(true)}
      onPointerEnter={() => setLookedAt(true)}
      className={cn("group block rounded-2xl", press, focusHaloFlush, className)}
    >
      <span className="@container relative isolate block pt-[3.2%] [--sheet-far:22%] [--sheet-near:40%] dark:[--sheet-far:27%] dark:[--sheet-near:47%]">
        <Sheets thumbhash={collection.cover?.thumbhash ?? null} />
        <span
          className="relative block overflow-hidden rounded-2xl bg-background shadow-[0_-3px_8px_-4px_rgb(0_0_0/0.12)] ring-1 ring-foreground/10 dark:shadow-[0_-3px_8px_-4px_rgb(0_0_0/0.45)]"
          style={{ aspectRatio: coverShape(cover) }}
        >
          {cover ? (
            <span className="absolute inset-0 transition-transform duration-500 group-hover:scale-[1.03]">
              <AssetPicture
                picture={cover}
                sizes={sizes}
                sensitive={sensitive}
                lookedAt={lookedAt}
              />
            </span>
          ) : (
            <span className="absolute inset-0 flex items-center justify-center text-muted-foreground">
              <Folder aria-hidden className="size-7" strokeWidth={1.5} />
            </span>
          )}
          {collection.visibility === "private" && (
            <span className="absolute top-2 right-2 flex size-6 items-center justify-center rounded-full bg-black/55 text-white">
              <Lock aria-label="Private" className="size-3" />
            </span>
          )}
        </span>
      </span>
      <span className="block px-0.5 pt-2">
        <span className="flex items-start gap-1.5">
          <TruncatedText
            text={collection.title}
            lines={2}
            className="min-w-0 text-sm font-medium text-pretty"
          />
          {collection.official && (
            <BadgeCheck aria-label="Official collection" className="mt-0.5 size-3.5 shrink-0" />
          )}
        </span>
        <span className="mt-0.5 block truncate text-xs text-muted-foreground">
          {collection.official ? "Official" : collection.owner?.displayName} ·{" "}
          {collection.assetCount} {collection.assetCount === 1 ? "asset" : "assets"}
        </span>
      </span>
    </Link>
  );
}

/** Waiting collections, at the shapes covers usually are. */
const WAITING_COVERS = [1.5, 0.75, 1.33, 1, 1.78, 0.8, 1.5, 1.2, 2, 0.75].map((shape, at) => ({
  id: `${at}`,
  shape,
}));

function waitingCollectionHeight(cover: { shape: number }, columnWidth: number): number {
  return columnWidth * SHEETS + columnWidth / cover.shape + CAPTION;
}

/**
 * Collections while they load, laid out by the same columns as the cards that
 * will replace them, on the same stack of sheets, so nothing moves when they
 * arrive.
 */
export function CollectionGridSkeleton() {
  return (
    <div aria-busy>
      <Masonry
        items={WAITING_COVERS}
        keyOf={cover => cover.id}
        heightOf={waitingCollectionHeight}
        render={cover => (
          <div>
            <div className="@container relative isolate pt-[3.2%]">
              <Sheets thumbhash={null} />
              {/* Opaque under its pulse, so the sheets don't show through it. */}
              <div
                className="relative rounded-2xl bg-background"
                style={{ aspectRatio: cover.shape }}
              >
                <Skeleton className="absolute inset-0 rounded-2xl" />
              </div>
            </div>
            <div className="px-0.5 pt-2">
              <Skeleton className="h-3.5 w-3/4 rounded" />
              <Skeleton className="mt-2 h-3 w-1/2 rounded" />
            </div>
          </div>
        )}
      />
    </div>
  );
}
